import React, { useState, useEffect, useRef, useCallback } from "react";
import scoreIncreaseSound from "../../Assets/sound/smooth movement/correct-choice-43861.mp3";
import { useNavigate } from "react-router";
import AuthUser from "../../Hooks/authUser";
import moment from "moment";
import PostHooks from "../../Hooks/PostHooks";
import { server_url } from "../../Config/API";
import UpdateHooks from "../../Hooks/UpdateHooks";
import GameStatsIndicator from "./GameStatsIndicator";

const ColorTrap = () => {
  const [gameStarted, setGameStarted] = useState(true);
  const [objectPosition, setObjectPosition] = useState({ x: 0, y: 0 });
  const [circleSize, setCircleSize] = useState(100);
  const navigate = useNavigate();
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [timeLeft, setTimeLeft] = useState(300);
  const [score, setScore] = useState(0);
  const [colorTrapData, setColorTrapData] = useState([]);

  const { userInfo } = AuthUser();
  const date = useRef(moment().format("YYYY-MM-D")).current;
  const time = useRef(moment().format("h:mm a")).current;

  const previousStateRef = useRef(null);
  const scoreRef = useRef(score);
  const timeRef = useRef(timeLeft);
  const circleIntervalRef = useRef(null);
  const circleTimeoutRef = useRef(null);
  const audioRef = useRef(null);

  // Keep refs in sync with state
  scoreRef.current = score;
  timeRef.current = timeLeft;

  // Lazily load Audio to prevent layout blocking and handle SSR if needed
  if (!audioRef.current && typeof window !== "undefined") {
    audioRef.current = new Audio(scoreIncreaseSound);
  }

  const fetchFunction = async () => {
    try {
      const response = await fetch(
        `${server_url}/colorTrap/specific?fieldName=userId&fieldValue=${
          userInfo?._id
        }&fieldName2=date&fieldValue2=${date}&fieldName3=gameName&fieldValue3=Color+Trap+Monocular`
      );
      const data = await response.json();
      setColorTrapData(data?.data || []);
      if (data?.data && data?.data.length) {
        setScore(data?.data[0]?.score);
        previousStateRef.current = data?.data;
      }
    } catch (error) {
      console.error("Error fetching color trap data:", error);
    }
  };

  useEffect(() => {
    if (colorTrapData?.length) {
      setTimeLeft(colorTrapData[0]?.remainingTime);
      setScore(colorTrapData[0]?.score);
    }
  }, [colorTrapData]);

  useEffect(() => {
    fetchFunction();
    return () => {
      if (circleIntervalRef.current) clearInterval(circleIntervalRef.current);
      if (circleTimeoutRef.current) clearTimeout(circleTimeoutRef.current);
    };
  }, []);

  const insertFunction = useCallback(() => {
    if (previousStateRef?.current?.length) {
      UpdateHooks(
        `${server_url}/colorTrap/updateColorTrap/${previousStateRef?.current[0]?._id}`,
        {
          date,
          time,
          score: scoreRef.current,
          remainingTime: timeRef.current,
        }
      );
    } else {
      PostHooks(
        `${server_url}/colorTrap/addColorTrap`,
        {
          gameName: "Color Trap Monocular",
          userId: userInfo?._id,
          userName: userInfo?.name,
          date,
          time,
          score: scoreRef.current,
          remainingTime: timeRef.current,
        },
        "Color Trap score updated !"
      );
    }
  }, [userInfo, date, time]);

  const insertFunctionRef = useRef(insertFunction);
  useEffect(() => {
    insertFunctionRef.current = insertFunction;
  }, [insertFunction]);

  const toggleFullScreenRef = useRef(null);

  const toggleFullScreen = () => {
    if (!isFullScreen) {
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen().catch((error) => {
          console.error("Error entering full screen:", error);
        });
      }
    }
    setIsFullScreen(!isFullScreen);
  };
  toggleFullScreenRef.current = toggleFullScreen;

  useEffect(() => {
    toggleFullScreen();
  }, []);

  const moveObjectRandomly = () => {
    const randomX = Math.random() * (window.innerWidth - 100);
    const randomY = Math.random() * (window.innerHeight - 100);
    setObjectPosition({ x: randomX, y: randomY });
    setCircleSize(100);
  };

  const handleTap = () => {
    if (gameStarted) {
      const proximity =
        Math.abs(objectPosition.x - window.innerWidth / 2) +
        Math.abs(objectPosition.y - window.innerHeight / 2);
      if (proximity <= 10) {
        setScore((prev) => prev + 1);
        if (audioRef.current) {
          audioRef.current.currentTime = 0;
          audioRef.current.play().catch((err) => console.log(err));
        }
      }

      if (circleIntervalRef.current) clearInterval(circleIntervalRef.current);
      circleIntervalRef.current = setInterval(() => {
        setCircleSize((size) => Math.max(size - 1, 0));
      }, 2);

      if (circleTimeoutRef.current) clearTimeout(circleTimeoutRef.current);
      circleTimeoutRef.current = setTimeout(() => {
        if (circleIntervalRef.current) clearInterval(circleIntervalRef.current);
        moveObjectRandomly();
      }, 50);
    }
  };

  const handleGameOver = () => {
    navigate(-1);
    if (toggleFullScreenRef.current) toggleFullScreenRef.current();
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(interval);
          setGameStarted(false);
          insertFunctionRef.current();
          handleGameOver();
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleKeyPress = async (event) => {
      if (event.keyCode === 27 && isFullScreen) {
        insertFunctionRef.current();
        await navigate(-1);
        if (toggleFullScreenRef.current) toggleFullScreenRef.current();
      }
    };

    document.addEventListener("keydown", handleKeyPress);
    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [isFullScreen, navigate]);

  return (
    <div className="flex flex-col items-center justify-center h-screen bg-white inset-0 fixed text-black">
      <GameStatsIndicator score={score} timeLeft={timeLeft} />
      <div
        className="relative w-12 h-12 bg-black cursor-pointer rounded-full"
        style={{
          position: "absolute",
          top: `${objectPosition.y}px`,
          left: `${objectPosition.x}px`,
          transition: "top 0.3s ease-out, left 0.3s ease-out",
        }}
        onClick={handleTap}
      >
        <div
          className="absolute bg-transparent border-2 border-blue-600 rounded-full"
          style={{
            width: `${circleSize}px`,
            height: `${circleSize}px`,
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            transition: "width 0.3s ease-out, height 0.3s ease-out",
          }}
        >
          <div
            className="w-1 h-1 bg-red-500 rounded-full"
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              cursor: "pointer",
            }}
            onClick={() => setScore((prev) => prev + 1)}
          ></div>
        </div>
      </div>
    </div>
  );
};

export default ColorTrap;
